import React, { useState, useEffect, useCallback } from 'react';
import { 
  Phone, 
  Send, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Copy, 
  Check,
  MessageSquare,
  Shield,
  RefreshCw,
  Smartphone,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { useSMSRelay } from '../../../authentication_components/phoneVerificationContext';
import { useAdminTracker } from '../../../authentication_components/adminTrackerContext';
import { useAuth } from '../../../authentication_components/authContext';

// =================================================================
// SMSVerificationDialog Component
// =================================================================
const SMSVerificationDialog = ({ isOpen, onClose, business, onVerificationComplete }) => {
  const { sendVerificationSMSWithCode, getRelayHealthStatus, loading, error, clearMessages } = useSMSRelay();
  const { createTracker } = useAdminTracker();
  const { adminId } = useAuth();

  // State
  const [step, setStep] = useState(1); // 1: Initial, 2: Code Sent, 3: Verification Input
  const [sentCode, setSentCode] = useState(null);
  const [messageId, setMessageId] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [userInputCode, setUserInputCode] = useState('');
  const [verificationResult, setVerificationResult] = useState(null); // 'success' | 'failed' | null
  const [localError, setLocalError] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [sendCount, setSendCount] = useState(0);

  const COOLDOWN_SECONDS = 60;
  const MAX_SENDS = 3;

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen && business) {
      setStep(1);
      setSentCode(null);
      setMessageId(null);
      setCodeCopied(false);
      setUserInputCode('');
      setVerificationResult(null);
      setLocalError(null);
      setCooldown(0);
      setSendCount(0);
      clearMessages();
    }
  }, [isOpen, business, clearMessages]);

  // Cooldown timer
  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  // Get relay health status
  const relayHealth = getRelayHealthStatus();

  // Handle sending verification code
  const handleSendCode = useCallback(async () => {
    if (!business?.business_phone) {
      setLocalError('No hay número de teléfono registrado para este negocio.');
      return;
    }

    if (sendCount >= MAX_SENDS) {
      setLocalError(`Límite alcanzado. Máximo ${MAX_SENDS} intentos de envío.`);
      return;
    }

    setLocalError(null);
    setVerificationResult(null);

    try {
      const result = await sendVerificationSMSWithCode(
        business.business_phone,
        business.business_name
      );

      if (result.success) {
        setSentCode(result.verificationCode);
        setMessageId(result.messageId);
        setStep(2);
        setCooldown(COOLDOWN_SECONDS);
        setSendCount(prev => prev + 1);

        // Track the SMS send action
        try {
          await createTracker({
            admin_id: adminId || 'unknown',
            change_type: 'sms_verification_sent',
            change_reason: `SMS verification code sent to ${business.business_phone} for business ${business.business_registry_number} (${business.business_name})`
          });
        } catch (trackerErr) {
          console.error('[AdminTracker] Failed to track SMS send:', trackerErr);
        }
      }
    } catch (err) {
      setLocalError(err.message || 'Error al enviar el código de verificación.');
    }
  }, [business, sendCount, sendVerificationSMSWithCode, createTracker, adminId]);

  // Handle copying code to clipboard
  const handleCopyCode = useCallback(() => {
    if (sentCode) {
      navigator.clipboard.writeText(sentCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  }, [sentCode]);

  // Handle verification check
  const handleVerifyCode = useCallback(() => {
    if (userInputCode === sentCode) {
      setVerificationResult('success');
      setStep(3);
      
      // Notify parent of successful verification
      if (onVerificationComplete) {
        onVerificationComplete(business.business_registry_number, true);
      }

      // Track successful verification
      createTracker({
        admin_id: adminId || 'unknown',
        change_type: 'sms_verification_success',
        change_reason: `Phone ${business.business_phone} verified successfully for business ${business.business_registry_number} (${business.business_name})`
      }).catch(err => console.error('[AdminTracker] Failed to track verification:', err));

    } else {
      setVerificationResult('failed');
    }
  }, [userInputCode, sentCode, business, onVerificationComplete, createTracker, adminId]);

  // Handle resend
  const handleResend = useCallback(() => {
    setUserInputCode('');
    setVerificationResult(null);
    handleSendCode();
  }, [handleSendCode]);

  // Handle close
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen || !business) return null;

  return (
    <div 
      className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-[70] p-4" 
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-500" />
            Verificación SMS
          </h3>
          <button 
            onClick={handleClose} 
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Business Info */}
          <div className="mb-6 text-center">
            <h4 className="font-semibold text-gray-900 text-lg">{business.business_name}</h4>
            <p className="text-xs text-gray-500 font-mono mt-1">{business.business_registry_number}</p>
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
              <Phone className="h-4 w-4 text-gray-600" />
              <span className="font-medium text-gray-800">{business.business_phone || 'Sin teléfono'}</span>
            </div>
          </div>

          {/* Relay Status Indicator */}
          <div className={`mb-4 p-3 rounded-lg border flex items-center gap-2 text-sm ${
            relayHealth.status === 'online' 
              ? 'bg-green-50 border-green-200 text-green-800'
              : relayHealth.status === 'warning'
              ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <Smartphone className={`h-4 w-4 ${
              relayHealth.status === 'online' ? 'text-green-600' :
              relayHealth.status === 'warning' ? 'text-yellow-600' : 'text-red-600'
            }`} />
            <span>{relayHealth.message}</span>
          </div>

          {/* Step 1: Initial - Send Code */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Instrucciones:</strong> Al presionar "Enviar Código", se enviará un SMS con un código de 6 dígitos al teléfono del negocio. 
                  El código se mostrará aquí para que puedas verificar si el usuario lo recibe correctamente.
                </p>
              </div>

              {!business.business_phone && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-800">
                    Este negocio no tiene un número de teléfono registrado.
                  </p>
                </div>
              )}

              <button
                onClick={handleSendCode}
                disabled={loading || !business.business_phone || relayHealth.status === 'offline'}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-xl font-semibold shadow-sm 
                         hover:bg-blue-700 transition-all transform active:scale-95 
                         disabled:bg-gray-300 disabled:cursor-not-allowed
                         flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Enviar Código de Verificación
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 2: Code Sent - Show Code */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Success Banner */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-green-800">¡Código Enviado!</p>
                  <p className="text-sm text-green-700">El SMS está en camino al dispositivo relay.</p>
                </div>
              </div>

              {/* Code Display Box */}
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white text-center shadow-lg">
                <p className="text-xs uppercase tracking-wider opacity-80 mb-2">Código Enviado</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-4xl font-mono font-bold tracking-[0.3em]">
                    {sentCode}
                  </span>
                  <button
                    onClick={handleCopyCode}
                    className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                    title="Copiar código"
                  >
                    {codeCopied ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {codeCopied && (
                  <p className="text-xs mt-2 opacity-80">¡Copiado!</p>
                )}
              </div>

              {/* Message ID */}
              {messageId && (
                <p className="text-xs text-center text-gray-500">
                  ID del mensaje: <span className="font-mono">{messageId}</span>
                </p>
              )}

              {/* Verification Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="text-sm text-gray-600 mb-3 text-center">
                  Pida al usuario que le diga el código que recibió:
                </p>
                
                <input
                  type="text"
                  value={userInputCode}
                  onChange={(e) => {
                    setUserInputCode(e.target.value.replace(/\D/g, '').substring(0, 6));
                    setVerificationResult(null);
                  }}
                  placeholder="Ingrese el código del usuario"
                  maxLength={6}
                  className="w-full p-3 text-center text-2xl font-mono font-bold tracking-[0.3em] 
                           border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 
                           focus:ring-blue-200 outline-none transition-all"
                />

                {/* Verification Result */}
                {verificationResult === 'failed' && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="text-sm text-red-800 font-medium">
                      Código incorrecto. El número de teléfono no coincide.
                    </span>
                  </div>
                )}

                <button
                  onClick={handleVerifyCode}
                  disabled={userInputCode.length !== 6}
                  className="w-full mt-4 py-3 px-4 bg-green-600 text-white rounded-xl font-semibold 
                           shadow-sm hover:bg-green-700 transition-all transform active:scale-95 
                           disabled:bg-gray-300 disabled:cursor-not-allowed
                           flex items-center justify-center gap-2"
                >
                  <Shield className="h-5 w-5" />
                  Verificar Código
                </button>
              </div>

              {/* Resend Section */}
              <div className="border-t border-gray-200 pt-4 flex items-center justify-between">
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {cooldown > 0 ? (
                    <span>Reenviar en {cooldown}s</span>
                  ) : (
                    <span>Intentos: {sendCount}/{MAX_SENDS}</span>
                  )}
                </div>
                <button
                  onClick={handleResend}
                  disabled={loading || cooldown > 0 || sendCount >= MAX_SENDS}
                  className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 
                           disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Reenviar
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Verification Complete */}
          {step === 3 && verificationResult === 'success' && (
            <div className="text-center space-y-4">
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <div>
                <h4 className="text-xl font-bold text-green-800">¡Verificación Exitosa!</h4>
                <p className="text-sm text-gray-600 mt-2">
                  El código coincide. El número de teléfono ha sido verificado.
                </p>
              </div>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>{business.business_phone}</strong> pertenece al negocio <strong>{business.business_name}</strong>
                </p>
              </div>
              <button
                onClick={handleClose}
                className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-semibold 
                         hover:bg-gray-200 transition-all"
              >
                Cerrar
              </button>
            </div>
          )}

          {/* Error Display */}
          {(localError || error) && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{localError || error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SMSVerificationDialog;